/**
 * /privacy — a PAGE. AppLayoutRoute supplies the chrome.
 *
 * Real policy, not prompts. Every statement here describes something the
 * platform actually does and can be checked against the code: the backup
 * schedule and retention in features/database, the uptime history window in
 * features/uptime, the processors in the deployment environment, and the
 * absence of any analytics or advertising script in the web app.
 *
 * Keep it that way. If the system changes — a new processor, a different
 * retention — this page is part of the change, not a document to reconcile
 * afterwards.
 */
import { LegalPage } from '../components/LegalPage';
import { brand } from '@/shared/brand';

export default function PrivacyPage() {
  return (
    <LegalPage
      title="Privacy policy"
      description="What Bloomneo collects, why, who processes it (DigitalOcean, Wasabi, Resend), how long it is kept, and how to have it corrected or deleted."
      updated="20 September 2026"
      draft={false}
      intro={
        <>
          {brand.name} is operated by Krishna Teja GS, Hyderabad, India, who is the data
          controller for the personal data described here. Questions, requests and complaints go to{' '}
          <a href="mailto:support@bloomneo.com" className="text-primary underline-offset-4 hover:underline">
            support@bloomneo.com
          </a>
          .
        </>
      }
      sections={[
        {
          heading: 'What we collect',
          body: (
            <>
              <p>
                <strong>Account details.</strong> Your name, email address and a hashed password.
                We never store a password in a form we can read.
              </p>
              <p className="mt-3">
                <strong>What you send us.</strong> Anything you write in the contact form or in
                support correspondence, including whatever you choose to include in it.
              </p>
              <p className="mt-3">
                <strong>A record of administrative actions.</strong> Who changed what and when —
                a deploy, a DNS record, a database restore. This is a security record, and it names
                the person who acted.
              </p>
              <p className="mt-3">
                <strong>Operational data about the infrastructure we run for you.</strong> Server
                metrics, uptime check results, certificate expiry dates, deployment logs and
                database sizes. This is about machines rather than people, but a deployment log can
                carry a name, so it is covered here too.
              </p>
              <p className="mt-3">
                <strong>Web server request logs.</strong> IP address, timestamp, the page
                requested. Ordinary server logs, kept for security and diagnosis.
              </p>
            </>
          ),
        },
        {
          heading: 'Why we collect it, and on what basis',
          body: (
            <>
              <p>
                Account details and operational data are processed to provide the service you have
                asked us to provide — performance of a contract. There is no version of managing a
                server that does not involve holding the details of it.
              </p>
              <p className="mt-3">
                Administrative records, request logs and monitoring are processed for security and
                to keep the service running — our legitimate interest in operating a platform other
                people depend on, and yours in it not being tampered with.
              </p>
              <p className="mt-3">
                We do not profile you, we do not make automated decisions about you, and we do not
                use your data to train models.
              </p>
            </>
          ),
        },
        {
          heading: 'Who else processes it',
          body: (
            <>
              <p>Three processors, each doing one thing:</p>
              <ul className="mt-3 list-disc space-y-1.5 pl-5">
                <li>
                  <strong>DigitalOcean</strong> — the servers and DNS your applications run on.
                </li>
                <li>
                  <strong>Wasabi</strong> — object storage holding database backups and files.
                </li>
                <li>
                  <strong>Resend</strong> — delivery of transactional email such as alerts and
                  password resets.
                </li>
              </ul>
              <p className="mt-3">
                We do not sell personal data, share it with advertising networks, or pass it to
                anyone else except where the law requires it. Some of these providers operate
                outside India; where that happens, the transfer relies on the provider's standard
                contractual terms.
              </p>
            </>
          ),
        },
        {
          heading: 'How long we keep it',
          body: (
            <ul className="list-disc space-y-1.5 pl-5">
              <li>Database backups: 30 days, then deleted automatically.</li>
              <li>Uptime check history: 31 days.</li>
              <li>Administrative records: for as long as the account is open, as a security record.</li>
              <li>Support correspondence: for as long as it is useful to the relationship, and deleted on request.</li>
              <li>
                Account details: until you close the account. After that we remove them within 30
                days, except anything we must keep for tax or legal reasons.
              </li>
            </ul>
          ),
        },
        {
          heading: 'Your rights',
          body: (
            <>
              <p>
                You can ask us for a copy of the personal data we hold about you, to correct it, to
                export it, or to delete it. You can object to processing based on legitimate
                interests, and you can complain to a supervisory authority.
              </p>
              <p className="mt-3">
                Write to{' '}
                <a href="mailto:support@bloomneo.com" className="text-primary underline-offset-4 hover:underline">
                  support@bloomneo.com
                </a>
                . We answer within 30 days and will say so if a request needs longer. There is no
                charge, and we will not make you explain why.
              </p>
            </>
          ),
        },
        {
          heading: 'Cookies',
          body: (
            <p>
              A session cookie that keeps you signed in, and nothing else. No analytics, no
              advertising, no third-party trackers — so there is nothing to consent to and no
              banner asking you to. The <a href="/cookies" className="text-primary underline-offset-4 hover:underline">cookie policy</a>{' '}
              says the same thing at more length.
            </p>
          ),
        },
        {
          heading: 'Changes',
          body: (
            <p>
              When the way we handle data changes, this page changes with it and the date at the
              top moves. If a change materially affects you — a new processor, a longer retention —
              we will tell account holders by email rather than relying on you to re-read it.
            </p>
          ),
        },
      ]}
    />
  );
}
